import classNames from 'classnames';
import { useSelectLabelForProductBookButton, useTrans } from 'core/hooks';
import {
  getProductMenuListPreview,
  selectActiveProduct,
} from 'core/redux/features/products/reducers/products';
import { AppState } from 'core/redux/reducers';
import React, { RefObject, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Column, Columns } from '../../../Components/Elements';
import TextEllipsis from '../../../Components/TextEllipsis';
import BookButton from '../Components/BookButton';
import PlainMenuList from '../Components/PlainMenusList';
import { ProductAddress } from '../Components/ProductAddress';
import ProductImage from '../Components/ProductImage';
import { ProductTimeList } from '../Components/ProductTimes';
import { OverlayTextDecider } from '../OverlayText';
import { ViewGalleryOverlay } from '../Page/Components/Gallery';
import { selectViewModes } from '../ViewModes';
import { usePrevious } from '../hooks';

interface IOwnProps {
  previewRef?: RefObject<HTMLDivElement>;
  onClickSeeMore: () => void;
  onClickMenu: () => void;
}

type TProps = IOwnProps;

function ProductPreview({ previewRef, onClickMenu, onClickSeeMore }: TProps) {
  const trans = useTrans();
  const { deviceType, viewInfo, product, menuPreviews, viewModes } =
    useSelector((state: AppState) => ({
      product: selectActiveProduct(state),
      viewInfo: state.products.viewInfo,
      deviceType: state.deviceType,
      activeViewType: state.viewTypes.activeViewType,
      menuPreviews: getProductMenuListPreview(state),
      areas: state.filters.areas,
      viewModes: selectViewModes(state),
    }));

  const handleClickMenu = useCallback(
    (_e, displayKey) => {
      if (!product) {
        return;
      }

      const menuPreview = menuPreviews[displayKey];
      if (!menuPreview) {
        return;
      }

      // let productMenuId =
      //   product?.menus.find(menuId => menuId === menuPreview.id) ||
      //   product?.menus[0]

      if (onClickMenu) {
        onClickMenu();
      }
    },
    [menuPreviews, onClickMenu, product],
  );
  console.log(menuPreviews);
  const cheapestMenu = menuPreviews[0];

  const prevProduct = usePrevious(product);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      deviceType === 'mobile' &&
      !viewInfo &&
      prevProduct.current?.status !== 'in-stock'
    ) {
      window.scroll(0, 0);
    }

    if (viewInfo) {
      prevProduct.current = product;
    }

    // eslint-disable-next-line
  }, []);

  const bookingButtonLabel = useSelectLabelForProductBookButton({
    product,
  });

  return (
    <>
      <div
        id="product-preview-display"
        className="product-content product-content--display is-hidden-mobile"
        ref={previewRef}
      >
        <div className="product-display product-preview">
          <Columns gap={false} className="no-mr">
            <Column size={['8-desktop', '7-tablet']}>
              <div className="overlay-wrap">
                <ProductImage
                  className="image-preview"
                  isLazy={false}
                  productId={product?.id}
                />
                {product && <OverlayTextDecider productId={product?.id} />}
                <div className="overlay">
                  <ViewGalleryOverlay product={product} />
                </div>
              </div>
            </Column>
            <Column size={['4-desktop', '5-tablet']}>
              <div className="product-info">
                <div className="items idesc">
                  <div className="eb-ellipsis">
                    <Link
                      onClick={_e =>
                        product && onClickSeeMore && onClickSeeMore()
                      }
                      to={`${product?.route}#${viewModes.desc.route}`}
                    >
                      <h1 className="name">{product?.name ?? '...'}</h1>
                    </Link>
                    <div className="spacer-xs" />
                  </div>
                  <ProductAddress product={product} />
                  <TextEllipsis className="desc">
                    {product?.short_description}
                  </TextEllipsis>
                  <div className="spacer-s" />
                  <div className="product_info__times">
                    <p className="label">
                      {trans(`times_of_today_${product?.product_type}`)}
                    </p>
                    {product ? (
                      <ProductTimeList
                        label={trans(`times_of_today_${product?.product_type}`)}
                        disabledEllipsis={true}
                        product={product}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="items imenus">
                  <Link
                    to={`${product?.route}#${
                      viewModes.menu.route === 'desc'
                        ? ''
                        : viewModes.menu.route
                    }`}
                    onClick={e => handleClickMenu(e, 0)}
                    className="show-menu link-label"
                  >
                    {trans('show_menus')} <span className="eb eb-rl" />
                  </Link>
                  {product && product?.menus.length > 0 ? (
                    <PlainMenuList
                      route={`${product?.route}#${viewModes.menu.route}`}
                      onClickMenu={handleClickMenu}
                      menuPreviews={menuPreviews}
                    />
                  ) : null}
                </div>
                <div className="items ilinks">
                  <div className="ilinks__item">
                    <Link
                      to={`${product?.route}#${viewModes.desc.route}`}
                      onClick={_e =>
                        product && onClickSeeMore && onClickSeeMore()
                      }
                      className="link-label"
                    >
                      {trans(viewModes.desc.transKey)}{' '}
                      <span className="eb eb-rl" />
                    </Link>
                  </div>
                </div>
                <div className="items ibook">
                  <div className="cheapest-menu">
                    <BookButton>
                      <span className="cheapest-menu__normal">
                        {cheapestMenu?.price_regular ?? '...'}
                        <span className="cheapest-menu__sign">kr</span>
                      </span>
                      <span className="cheapest-menu__sale">
                        {cheapestMenu?.price_sale ?? '...'}
                        <span className="cheapest-menu__sign"> kr</span>
                      </span>
                    </BookButton>
                  </div>
                  <BookButton
                    className={classNames(
                      'button is-fullwidth is-padded',
                      product?.status !== 'in-stock'
                        ? 'is-grey-dark has-text-white disabled'
                        : 'is-red-dark',
                    )}
                  >
                    <span className="has-letter-space is-uppercase is-ellapsis">
                      {bookingButtonLabel}
                    </span>
                  </BookButton>
                </div>
              </div>
            </Column>
          </Columns>
        </div>
      </div>
    </>
  );
}

export default ProductPreview;
